/**
 * How each channel LOOKS and hands off on the frontend — the one place channel
 * ids are enumerated on this side (D1: a channel id in a conditional outside the
 * registry layer is a review finding). A channel with no entry here still renders
 * (default glyph, no hand-off) — that is what lets a test-registered channel show
 * up in every picker with zero surface code.
 */
import type { ComponentType } from 'react';
import { Globe, Mail, MessageCircle, MessageSquare } from 'lucide-react';

import { WHATSAPP_BRAND_TEXT_CLASS, WhatsAppIcon } from '../components/icons';
import { buildContactMailtoUrl } from '../email/handoff';
import { getEntityUrl } from '../utils/entityUrls';
import { buildContactWhatsAppUrl } from '../whatsapp/handoff';
import type {
  CommsChannelId,
  CommsHandoffOptions,
  CommsPartyLookup,
  CommsRawAddress,
} from './types';

export interface ChannelPresentation {
  Icon: ComponentType<{ className?: string }>;
  /** Colour class for the glyph when the entry is enabled (brand hue where one exists). */
  brandClass?: string;
  /** Which part of a raw address this channel delivers to. */
  addressFrom?: (address: CommsRawAddress) => string | null;
  /** The client hand-off (`client_handoff` capability): mailto:, wa.me. */
  buildHandoffUrl?: (address: string, opts: CommsHandoffOptions) => string;
  /** i18n key (common namespace) shown under the hand-off entry. */
  handoffHintKey?: string;
  /**
   * How an in-app send action relates to the hand-off entry: `replace` swaps the
   * single entry's action to in-app (email: composer instead of mailto), `extra`
   * adds a second entry beside the hand-off (WhatsApp: the business number beside
   * the clicker's own wa.me).
   */
  inAppMode: 'replace' | 'extra';
  /** Deep link to the party's in-app compose surface for this channel, when one exists. */
  composeUrl?: (contactId: string) => string | null;
}

const DEFAULT_PRESENTATION: ChannelPresentation = {
  Icon: MessageCircle,
  inAppMode: 'replace',
};

const PRESENTATION: Partial<Record<CommsChannelId, ChannelPresentation>> = {
  email: {
    Icon: Mail,
    addressFrom: (address) => address.email?.trim() || null,
    buildHandoffUrl: (address, opts) =>
      buildContactMailtoUrl(address, { subject: opts.subject, body: opts.text }),
    inAppMode: 'replace',
  },
  whatsapp: {
    Icon: WhatsAppIcon,
    brandClass: WHATSAPP_BRAND_TEXT_CLASS,
    addressFrom: (address) => address.phone?.trim() || null,
    buildHandoffUrl: (address, opts) => buildContactWhatsAppUrl(address, opts.text),
    handoffHintKey: 'comms.handoff.whatsappOwnAccount',
    inAppMode: 'extra',
    composeUrl: (contactId) => {
      const base = getEntityUrl({ entityType: 'contacts', entityId: contactId });
      return base ? `${base}?tab=correspondence` : null;
    },
  },
  wechat: { Icon: MessageSquare, inAppMode: 'replace' },
  portal: { Icon: Globe, inAppMode: 'replace' },
};

export function presentationFor(id: CommsChannelId): ChannelPresentation {
  return PRESENTATION[id] ?? DEFAULT_PRESENTATION;
}

/**
 * Channels a surface can offer before (or without) the registry answering: the
 * ones with a client hand-off. Used as the offline fallback so a failed
 * `/comms/channels` degrades to today's mailto / wa.me menu, never to an empty one.
 */
export const HANDOFF_CHANNEL_IDS: readonly CommsChannelId[] = (
  Object.keys(PRESENTATION) as CommsChannelId[]
).filter((id) => PRESENTATION[id]?.buildHandoffUrl !== undefined);

/** The party's address for `id`, from its raw address alone (synchronous). */
export function rawAddressFor(id: CommsChannelId, party: CommsPartyLookup | undefined): string | null {
  const address = party?.address;
  if (!address) return null;
  return presentationFor(id).addressFrom?.(address) ?? null;
}

/** True when at least one hand-off channel has an address for this party. */
export function hasAnyHandoffAddress(party: CommsPartyLookup | undefined): boolean {
  return HANDOFF_CHANNEL_IDS.some((id) => rawAddressFor(id, party) !== null);
}

